import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  Skeleton,
  Chip,
  Alert,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { ValueContent } from 'app/apiService/valueContent/valueContentApiService';
import { formatDateForDisplay } from 'utils/dateUtils';
import ValueForm, { ValueFormData, ValueFormErrors } from './components/ValueForm';

interface DealerValueDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: ValueContent;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (updated: ValueContent) => Promise<void>;
  isEditing: boolean;
  setIsEditing: (val: boolean) => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
}

const DealerValueDetailsModal: React.FC<DealerValueDetailsModalProps> = ({
  open,
  onClose,
  data,
  onEdit,
  onDelete,
  onSave,
  isEditing,
  setIsEditing,
  setRefreshKey,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<ValueFormData>({
    headline: data.headline || '',
    content: data.content || '',
    value_type: data.value_type || '',
    applicable_for: data.applicable_for || [],
    end_date: data.end_date || '',
    is_active: data.is_active ?? true,
    label: data.label || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<ValueFormErrors>({});
  const [apiError, setApiError] = useState<string>('');

  React.useEffect(() => {
    setFormData({
      headline: data.headline || '',
      content: data.content || '',
      value_type: data.value_type || '',
      applicable_for: data.applicable_for || [],
      end_date: data.end_date || '',
      is_active: data.is_active ?? true,
      label: data.label || '',
    });
    setFormErrors({});
    setApiError('');
  }, [data]);

  React.useEffect(() => {
    if (!isEditing) {
      setFormErrors({});
      setApiError('');
    }
  }, [isEditing]);

  React.useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setFormErrors({});
      setApiError('');
    }
  }, [open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof ValueFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof ValueFormErrors];
        return newErrors;
      });
    }
    if (apiError) {
      setApiError('');
    }
  };

  const validateForm = (): boolean => {
    const errors: ValueFormErrors = {};

    if (!formData.headline?.trim()) {
      errors.headline = 'Headline is required';
    }
    if (!formData.content?.trim()) {
      errors.content = 'Message content is required';
    }
    if (!formData.value_type?.trim()) {
      errors.value_type = 'Value type is required';
    }
    if (formData.applicable_for.length === 0) {
      errors.applicable_for = 'At least one applicable option must be selected';
    }
    
    // Validate label field - optional but must be under 40 characters if provided
    if (formData.label && formData.label.length > 40) {
      errors.label = 'Label must be 40 characters or less';
    }
    
    // Validate expiry date - now required
    if (!formData.end_date?.trim()) {
      errors.end_date = 'Expiry date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError('');
    try {
      const updatedData: ValueContent = {
        ...data,
        headline: formData.headline.trim(),
        content: formData.content.trim(),
        value_type: formData.value_type,
        applicable_for: formData.applicable_for,
        end_date: formData.end_date || undefined,
        is_active: formData.is_active,
        label: formData.label?.trim() || undefined,
      };
      await onSave(updatedData);
      setRefreshKey(prev => prev + 1);
      setFormErrors({});
      onClose(); // Close modal after successful save
    } catch (error) {
      setApiError(
        error instanceof Error ? error.message : 'Failed to save value content',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      headline: data.headline || '',
      content: data.content || '',
      value_type: data.value_type || '',
      applicable_for: data.applicable_for || [],
      end_date: data.end_date || '',
      is_active: data.is_active ?? true,
      label: data.label || '',
    });
    setIsEditing(false);
    setFormErrors({});
    setApiError('');
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: '800px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header */}
        <Box
          className="modal-header"
          sx={{
            p: 2,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" fontWeight="600">
              Dealer Value Details
            </Typography>
            <Box>
              {!isEditing && (
                <Tooltip title="Edit Dealer Value">
                  <IconButton
                    className="modal-header-button"
                    sx={{ p: 0.5 }}
                    size="small"
                    onClick={onEdit}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete Dealer Value">
                <IconButton
                  className="modal-header-button"
                  sx={{ p: 0.5, ml: 1 }}
                  size="small"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  className="modal-header-button"
                  aria-label="close"
                  onClick={onClose}
                  sx={{ p: 0.5, ml: 1 }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          {isEditing ? (
            <ValueForm
              formData={formData}
              onFormChange={handleFormChange}
              errors={formErrors}
              disabled={isSubmitting}
            />
          ) : (
            <>
              {/* Headline */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Headline
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.headline}
                </Typography>
              </Box>

              {/* Content */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Message
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.content}
                </Typography>
              </Box>

              {/* Label */}
              {data.label && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Label
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data.label}
                  </Typography>
                </Box>
              )}

              {/* Type */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Type
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.value_type?.toUpperCase().replace('_', ' ')}
                </Typography>
              </Box>

              {/* Applicable For */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Applicable For
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {data.applicable_for && data.applicable_for.length > 0 ? (
                    data.applicable_for.map((value: string) => (
                      <Chip key={value} label={value} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No applicable options selected
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Expiry Date */}
              {data.end_date && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Expiry Date
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {formatDateForDisplay(data.end_date)}
                  </Typography>
                </Box>
              )}

              {/* Active Status */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Status
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {data.is_active ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 9,
            flexShrink: 0,
            gap: 2,
          }}
        >
          {isEditing && (
            <>
              <ButtonMAIQ
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </ButtonMAIQ>
              {isSubmitting ? (
                <Skeleton
                  variant="rectangular"
                  width={140}
                  height={36}
                  sx={{
                    borderRadius: 1,
                    bgcolor: theme.palette.action.hover,
                  }}
                />
              ) : (
                <Tooltip title="Save your changes">
                  <ButtonMAIQ
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                  >
                    Save Changes
                  </ButtonMAIQ>
                </Tooltip>
              )}
            </>
          )}
        </Box>

        {/* Delete Confirmation Modal */}
        <ConfirmationDialogMAIQ
          open={deleteModalOpen}
          title="Delete Dealer Value"
          message="Are you sure you want to delete this dealer value? This action cannot be undone."
          onConfirm={async () => {
            try {
              await onDelete();
              setDeleteModalOpen(false); // Close confirmation modal after successful deletion
            } catch (error) {
              // Error is handled by parent component, but keep modal open if there's an error
              console.error('Error deleting value content:', error);
            }
          }}
          onClose={() => setDeleteModalOpen(false)}
        />
      </Paper>
    </ModalMAIQ>
  );
};

export default DealerValueDetailsModal;
